import React from "react";
import {Button} from "@/components/ui/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown";
import {Check, CheckCheck, ExternalLink, ListChecks} from "lucide-react";
import {useSave} from "@/contexts/SaveContext";
import {WorkflowNodesData} from "../NodeTypes/NodesData";
import {useSelectedElements} from "@/hooks/useSelectedElements";
import {Icon} from "@/components/ui/icon";
import {Tooltip, TooltipContent, TooltipTrigger} from "@/components/ui/tooltip";

const Checklist = () => {
  const {flowIssues} = useSave();
  const {setSelectedNode} = useSelectedElements();
  const hasIssues = flowIssues.length > 0;

  return (
    <DropdownMenu>
      <Tooltip delayDuration={0} disableHoverableContent={true}>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              className="[&_svg]:size-5 px-2 relative"
            >
              <ListChecks />
              {hasIssues && (
                <span className="text-xs absolute -top-2 -right-2 w-5 h-5 border rounded-full bg-white text-label flex items-center justify-center">
                  {flowIssues.length}
                </span>
              )}
            </Button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent side="bottom" sideOffset={10}>
          Checklist
        </TooltipContent>
      </Tooltip>
      <DropdownMenuContent
        align="end"
        className="rounded-xl p-4 w-80 bg-white text-primary border border-border flex flex-col"
      >
        <div className="mb-3">
          <h4 className="text-base text-primary font-semibold">Checklist</h4>
          <p className="text-sm text-secondary">
            Resolve all issues before publishing.
          </p>
        </div>
        {hasIssues ? (
          <div className="flex flex-col gap-2">
            {flowIssues.map(issue => {
              const nodeConfig = WorkflowNodesData[issue.nodeType];
              const nodeIcon = nodeConfig?.icon;

              const nodeStyles = {
                icon: {
                  color: nodeConfig.styles.iconColor,
                  backgroundColor: nodeConfig.styles.iconBackgroundColor,
                  borderColor: nodeConfig.styles.iconBorderColor,
                },
              };

              return (
                <DropdownMenuItem
                  key={issue.nodeId}
                  className="p-2 flex flex-col hover:bg-slate-50 border border-border rounded-lg"
                  onClick={() => {
                    setSelectedNode(issue.nodeId);
                  }}
                >
                  <div className="flex gap-2 w-full justify-between">
                    <div
                      style={nodeStyles.icon}
                      className="mr-auto py-1 px-2  flex gap-2 items-center rounded border-0"
                    >
                      {nodeIcon && React.createElement(nodeIcon)}
                      {issue.nodeTitle}
                    </div>
                    <ExternalLink
                      className="size-4 text-disabled"
                      strokeWidth={1.5}
                    />
                  </div>
                  <div
                    className="overflow-hidden text-ellipsis max-w-full"
                    title={issue.message}
                  >
                    {issue.message}
                  </div>
                </DropdownMenuItem>
              );
            })}
          </div>
        ) : (
          <div className="p-3 flex flex-col border border-border rounded-lg items-center">
            <Icon className="size-14 bg-green bg-opacity-8 rounded-lg border border-green border-opacity-16 mb-4">
              <CheckCheck className="text-green size-8" />
            </Icon>
            <p className="text-base font-medium mb-0.5">There's no issues</p>
            <p className="text-sm text-secondary">
              Your flow is ready to be published
            </p>
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default Checklist;
